"use client"

import { useEffect, useRef, useState } from "react"
import { useInView } from "react-intersection-observer"
import type { components } from "umbraco"
import { getMediaUrl } from "umbraco"
import { getNonUmbracoUrl } from "utils"
import { getUmbracoMediaUrl } from "utils/getUmbracoMediaUrl"
import Container from "../../Container"
import PauseIcon from "../../icons/PauseIcon"
import PlayIcon from "../../icons/PlayIcon"
import { useInViewConfig } from "../../transitions/utils"
import {
  Root,
  TitleBar,
  Title,
  PauseButton,
  MarqueeSection,
  MarqueeRow,
  MarqueeTrack,
  LogoCard,
  LogoLink,
  LogoImage,
} from "./styles"

type LogosStripData = components["schemas"]["LogosStripBlockPropertiesModel"]
type LogoItemData = components["schemas"]["LogoItemPropertiesModel"]

interface Props {
  data: LogosStripData
  settings?: Record<string, unknown> | null
  locale: string
  culture: string
  [key: string]: unknown
}

function LogoCardItem({ item }: { item: LogoItemData }) {
  const desktopRel = getMediaUrl(item.logoImage)
  const imageUrl = desktopRel ? getUmbracoMediaUrl(desktopRel) : null

  if (!imageUrl) return null

  // EGDE-78: optional dedicated mobile logo; falls back to desktop when empty.
  const mobileRel = getMediaUrl(item.logoImageMobile)
  const mobileUrl = mobileRel ? getUmbracoMediaUrl(mobileRel) : null

  const alt = item.altText ?? ""
  const content = mobileUrl ? (
    <>
      <LogoImage src={imageUrl} alt={alt} loading="lazy" $hideOnMobile />
      <LogoImage src={mobileUrl} alt={alt} loading="lazy" $hideOnDesktop />
    </>
  ) : (
    <LogoImage src={imageUrl} alt={alt} loading="lazy" />
  )

  if (item.linkUrl) {
    const href = getNonUmbracoUrl(item.linkUrl)
    return (
      <LogoCard>
        <LogoLink
          href={href}
          target={item.openInNewTab ? "_blank" : undefined}
          rel={item.openInNewTab ? "noopener noreferrer" : undefined}
        >
          {content}
        </LogoLink>
      </LogoCard>
    )
  }

  return <LogoCard>{content}</LogoCard>
}

/* EGDE-502: the numbers below are measured off https://aurora.tech — the
   reference Raz asks the strip to match one-to-one (probe scripts and raw
   output in tasks/tickets/EGDE-502/).

   At 1440x900, aurora's two rows drift at ±6.9 px/s on their own, and each row
   translates 2.1px per pixel of page scroll along its own direction (so a
   scroll down speeds both rows up, a scroll up runs them backwards). The
   coupling is to scroll POSITION, not scroll speed: scrolling the same 600px
   over 3.4s and over 10.6s moved the logos 1435px and 1328px respectively —
   near-identical, so duration is not what drives it. One full pass of the
   section through the viewport carries each row about 7 card widths.

   Both constants are the measured reference values rather than an adjusted
   version of them: Raz's standard is "one-to-one similar to the reference", so
   aurora's own numbers are what that sentence asks for. The drift is also now a
   fixed px/s rather than a fixed time per logo set — the old per-set duration
   meant the speed changed whenever an editor added or removed a logo. Our cards
   are 357px against aurora's 360px, so the same px/s reads the same. */
const SCROLL_COUPLING = 2.1
const DRIFT = 7 // px/s
/* The reference lags the scroll rather than locking to it — after a flick its
   velocity decays over roughly a third of a second instead of stopping dead. */
const FOLLOW_TAU = 180 // ms

const wrap = (value: number, span: number) => ((value % span) + span) % span

/* EGDE-670: a row's logos are repeated up to this many cards before the set is
   tiled, so a row that ends up with one or two logos still spans more than a
   viewport (a set is ~127px per card on mobile, ~357px on desktop — see
   styles.ts). The seam maths needs the visible window to fit inside two sets;
   six desktop cards is ~2140px, which clears any screen we ship to. The client
   currently has 11 logos, so this only guards the "low amount of logos"
   scenario the ticket leaves undefined. */
const MIN_SET_CARDS = 6

/* One marquee row: `logos` is the rendered track (four identical sets, see
   below) and `setCards` is how many cards make up ONE set. */
interface RowTrack {
  logos: LogoItemData[]
  setCards: number
}

/*
 * EGDE-670: both rows used to render the SAME full logo list, so every logo
 * appeared twice on screen. The logos are now split across the rows — 11 logos
 * become 6 on top and 5 below — with each row tiling only its own half.
 *
 * Four identical sets per row, exactly as before: the loop advances one set, so
 * a spare set flanks the visible window on both sides and the track edge can
 * never come into view (see styles.ts).
 */
function buildRow(logos: LogoItemData[]): RowTrack {
  if (logos.length === 0) return { logos: [], setCards: 0 }
  const fill = Math.ceil(MIN_SET_CARDS / logos.length)
  const set = Array.from({ length: fill }, () => logos).flat()
  return { logos: [...set, ...set, ...set, ...set], setCards: set.length }
}

export default function LogosStrip({ data }: Props) {
  const [paused, setPaused] = useState(false)
  const pausedRef = useRef(paused)
  pausedRef.current = paused
  const sectionRef = useRef<HTMLDivElement>(null)
  const track1Ref = useRef<HTMLDivElement>(null)
  const track2Ref = useRef<HTMLDivElement>(null)
  const { ref: entranceRef, inView } = useInView(useInViewConfig)

  /* EGDE-510 point 5: the title has to enter from the SCREEN's edge, not from
     inside the grid. Measure the real distance from its resting inline-start
     edge to the viewport edge and publish it as --title-gap.

     Measured off the PARENT (Container), never off the title itself: the title
     already carries the entrance transform, so its own rect would report the
     displaced position and the gap would compound on every resize. The parent's
     content-box edge IS the title's resting edge. */
  const titleBarRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const bar = titleBarRef.current
    const parent = bar?.parentElement
    // Guarded like the FormShell observer — the CSS fallback keeps the title
    // visible if the measurement never runs.
    if (!bar || !parent || typeof ResizeObserver === "undefined") return
    const update = () => {
      const rect = parent.getBoundingClientRect()
      const cs = getComputedStyle(parent)
      // Trust the dir attribute — computed direction reads inverted here.
      const rtl = document.documentElement.getAttribute("dir") !== "ltr"
      const pad = parseFloat(rtl ? cs.paddingRight : cs.paddingLeft) || 0
      const gap = rtl ? window.innerWidth - (rect.right - pad) : rect.left + pad
      // Mobile's grid inset is only 16px, which would read as a twitch rather
      // than an entrance; keep a floor so the sweep is legible at every width.
      bar.style.setProperty("--title-gap", `${Math.max(64, Math.round(gap))}px`)
    }
    update()
    const observer = new ResizeObserver(update)
    observer.observe(parent)
    window.addEventListener("resize", update)
    return () => {
      observer.disconnect()
      window.removeEventListener("resize", update)
    }
  }, [])

  // EGDE-603: the strip has no animation toggle. EGDE-318 gave it one, but the
  // marquee is the block's whole design (EGDE-502, Raz) — a static logo wall is
  // not an approved state, so QA asked for the field to go. The toggle is removed
  // from the CMS element and the marquee always runs (still yielding to
  // prefers-reduced-motion, and to the visitor's pause button).

  // EGDE-478 removed the EGDE-318 scroll-coupled drift after QA flagged it, and
  // EGDE-502 puts it back deliberately: Raz (design) has since specified the
  // aurora.tech behaviour three times, most recently "still does not match the
  // reference — feel the scrolling with the mouse beyond the automatic playback
  // of the logos". Scroll coupling IS the reference behaviour; what EGDE-478
  // actually saw was the unsmoothed, un-gated EGDE-318 version.
  const logoItems = (data.logos?.items ?? [])
    .map((block) => block.content.properties as LogoItemData | undefined)
    .filter((p): p is LogoItemData => !!p)

  /* EGDE-670: split the logos between the two rows instead of repeating the
     whole list in both — "if we have 11 logos, 6 will appear in one row and 5 in
     the other". The top row takes the larger half so an odd count leans upward.
     A single logo is the one split we can't make: give it to both rows rather
     than leaving the second one empty. */
  const half = Math.ceil(logoItems.length / 2)
  const [topRow, bottomRow] =
    logoItems.length > 1
      ? [logoItems.slice(0, half), logoItems.slice(half)]
      : [logoItems, logoItems]
  const track1 = buildRow(topRow)
  const track2 = buildRow(bottomRow)

  // The rAF driver replaces the CSS keyframes at runtime; the keyframes stay as
  // the no-JS fallback.
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return
    const t1 = track1Ref.current
    const t2 = track2Ref.current
    const section = sectionRef.current
    if (!t1 || !t2 || !section) return

    // JS owns the transform from here — the CSS animation would override
    // inline transforms while running.
    t1.style.animation = "none"
    t2.style.animation = "none"

    // stylis-rtl mirrors the keyframe translateX in RTL; mirror the rAF
    // directions the same way so the rows keep their current travel per
    // locale. (Trust the dir attribute — computed direction reads inverted.)
    const isRtl = document.documentElement.getAttribute("dir") === "rtl"

    /* One set = a quarter of that row's 4-set track. EGDE-670: measured per
       track — the rows now hold different halves of the logo list, so a 6-logo
       set and a 5-logo set no longer share a width. Reusing row 1's width for
       row 2 would leave row 2 wrapping mid-set and jumping every loop. */
    let setW1 = 0
    let setW2 = 0
    const measure = () => {
      setW1 = t1.scrollWidth / 4
      setW2 = t2.scrollWidth / 4
    }
    measure()
    window.addEventListener("resize", measure)

    // Only the scroll that happens while the strip is on screen moves it — off
    // screen the page can travel thousands of pixels, and banking all of that
    // would fling the rows the moment they appeared.
    let onScreen = true
    const io = new IntersectionObserver(
      ([entry]) => {
        onScreen = entry.isIntersecting
      },
      { rootMargin: "100px 0px" },
    )
    io.observe(section)

    let scrollTarget = 0
    let scrollOffset = 0
    let lastScrollY = window.scrollY
    const onScroll = () => {
      const delta = window.scrollY - lastScrollY
      lastScrollY = window.scrollY
      if (!onScreen || pausedRef.current) return
      scrollTarget += delta * SCROLL_COUPLING
    }
    window.addEventListener("scroll", onScroll, { passive: true })

    let raf = 0
    let drift = 0
    let last = performance.now()
    const tick = (now: number) => {
      const dt = Math.min(100, now - last)
      last = now
      if (!pausedRef.current && setW1 > 0 && setW2 > 0) {
        drift += (DRIFT * dt) / 1000
        scrollOffset +=
          (scrollTarget - scrollOffset) * (1 - Math.exp(-dt / FOLLOW_TAU))
        // Both rows advance by the same number of PIXELS — only the wrap span
        // differs — so unequal halves still travel at one speed.
        const travelled = drift + scrollOffset
        const p1 = wrap(travelled, setW1)
        const p2 = wrap(travelled, setW2)
        // Row 1 travels one set from -1×set toward -2×set (identical content
        // flanks both ends); row 2 runs the opposite way. RTL mirrors both,
        // matching the cssjanus-flipped keyframes it replaces.
        const x1 = isRtl ? setW1 + p1 : -(setW1 + p1)
        const x2 = isRtl ? 2 * setW2 - p2 : p2 - 2 * setW2
        t1.style.transform = `translate3d(${x1}px, 0, 0)`
        t2.style.transform = `translate3d(${x2}px, 0, 0)`
      }
      raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)

    return () => {
      cancelAnimationFrame(raf)
      io.disconnect()
      window.removeEventListener("resize", measure)
      window.removeEventListener("scroll", onScroll)
      t1.style.animation = ""
      t2.style.animation = ""
      t1.style.transform = ""
      t2.style.transform = ""
    }
  }, [])

  return (
    /* EGDE-510: the strip had no entrance at all (point 8) — r3 gave it the
       shared bottom-to-top rise, and point B1 rejected that: the texts must
       come in right-to-left and the logos left-to-right. Root only publishes
       the trigger; the two halves carry their own directional travel (see
       TitleBar / MarqueeSection in styles.ts). */
    <Root data-block="logos-strip" ref={entranceRef} $entered={inView}>
      <Container paddingMobile>
        <TitleBar ref={titleBarRef}>
          <Title variant="h3">{data.title}</Title>
          <PauseButton
            onClick={() => setPaused((prev) => !prev)}
            aria-label={paused ? "Play" : "Pause"}
            type="button"
          >
            {paused ? <PlayIcon /> : <PauseIcon />}
          </PauseButton>
        </TitleBar>
      </Container>

      <MarqueeSection ref={sectionRef}>
        {/* Point 5: row 1 enters from the inline-START edge (right in RTL),
            row 2 from the opposite edge. The wrapper owns the entrance so the
            rAF driver can keep writing the tracks' own transform. */}
        <MarqueeRow $fromStart>
          <MarqueeTrack
            ref={track1Ref}
            $paused={paused}
            $direction="left"
            $setCards={track1.setCards}
          >
            {track1.logos.map((item, i) => (
              <LogoCardItem key={`r1-${i}`} item={item} />
            ))}
          </MarqueeTrack>
        </MarqueeRow>
        <MarqueeRow>
          <MarqueeTrack
            ref={track2Ref}
            $paused={paused}
            $direction="right"
            $setCards={track2.setCards}
          >
            {track2.logos.map((item, i) => (
              <LogoCardItem key={`r2-${i}`} item={item} />
            ))}
          </MarqueeTrack>
        </MarqueeRow>
      </MarqueeSection>
    </Root>
  )
}
